'use client';

import { type ProductFilterSchema, productFilterSchema } from '@coas/validators/payload';
import { useEffect } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { useForm } from 'react-hook-form';
import type { RouterOutputs } from 'trpc/shared';
import IconArrowLeft from '~/shared/assets/icons/arrow-left.svg';
import IconChevronRight from '~/shared/assets/icons/chevron-right.svg';
import IconClose from '~/shared/assets/icons/close.svg';
import { Price } from '~/shared/ui/price';
import { filterItems } from '../config/filterItems';
import { useProductFilter } from '../model/hooks';
import styles from './ProductFilter.module.scss';
import FilterBrand from './filters/FilterBrand';
import FilterColor from './filters/FilterColor';
import FilterPrice from './filters/FilterPrice';
import FilterSize from './filters/FilterSize';

type ProductFilterProps = {
    category: RouterOutputs['category']['getBySlug'] | null;
    list?: RouterOutputs['list']['getBySlug'] | null;
};

export default ({ category, list }: ProductFilterProps) => {
    const [{ open, selected, filter }, setState, isDirty] = useProductFilter();

    const form = useForm<ProductFilterSchema>({
        defaultValues: filter,
    });
    const { reset, watch } = form;

    const [minPrice, maxPrice] = watch('price');

    useEffect(() => {
        if (!open) reset(filter);
    }, [open, reset, filter]);

    return (
        <ModalOverlay
            className={styles.modalOverlay}
            isOpen={open}
            onOpenChange={() => setState(prev => ({ ...prev, open: false }))}
            isDismissable
        >
            <Modal className={styles.modal}>
                <Dialog className={'min-h-screen w-full bg-neutral-50 text-neutral-950 '}>
                    <form
                        onSubmit={form.handleSubmit(data => {
                            setState(prev => ({
                                ...prev,
                                open: false,
                                selected: undefined,
                                filter: data,
                            }));
                        })}
                        className={'flex max-h-screen min-h-screen w-full flex-col gap-y-page py-page'}
                    >
                        <div className={'relative mx-page flex items-center justify-center bg-neutral-50'}>
                            <button
                                type={'button'}
                                className={'absolute left-0 block rounded-14 bg-neutral-200 p-2 md:hidden'}
                                onClick={() =>
                                    setState(prev =>
                                        prev.selected !== undefined
                                            ? { ...prev, selected: undefined }
                                            : { ...prev, open: false },
                                    )
                                }
                            >
                                {selected !== undefined ? (
                                    <IconArrowLeft className={'h-5 w-5 stroke-neutral-500'} />
                                ) : (
                                    <IconClose className={'h-5 w-5 stroke-neutral-500'} />
                                )}
                            </button>
                            <button
                                type={'button'}
                                className={'absolute left-0 hidden rounded-14 bg-neutral-200 p-2 md:block'}
                                onClick={() => setState(prev => ({ ...prev, open: false }))}
                            >
                                <IconClose className={'h-5 w-5 stroke-neutral-500'} />
                            </button>
                            <Heading slot='title' level={2} className='font-500 text-24'>
                                {filterItems.find(item => selected !== undefined && item.key === selected)?.title ??
                                    'Фильтры'}
                            </Heading>
                        </div>
                        <div className={'relative flex-1 flex-col overflow-auto px-page'}>
                            {selected === undefined &&
                                filterItems.map(item => {
                                    return (
                                        <button
                                            key={item.key}
                                            type={'button'}
                                            onClick={() => setState(prev => ({ ...prev, selected: item.key }))}
                                            className={'flex w-full items-center gap-x-4 py-2.5'}
                                        >
                                            <span className={'flex-1 space-x-3 px-page text-start'}>
                                                <span className={'text-neutral-950'}>{item.title}</span>
                                                <span className={'font-300 text-18 text-neutral-500'}>
                                                    {item.key !== 'price' && (filter[item.key]?.length || '')}
                                                    {item.key === 'price' && (
                                                        <>
                                                            <Price withoutCurrency>{minPrice}</Price>
                                                            {' — '}
                                                            <Price withoutCurrency>{maxPrice}</Price>
                                                        </>
                                                    )}
                                                </span>
                                            </span>
                                            <IconChevronRight className={'mb-1 h-5 w-5 stroke-neutral-400'} />
                                        </button>
                                    );
                                })}
                            {selected === 'colors' && (
                                <FilterColor key={'colors-filter'} category={category} list={list} form={form} />
                            )}
                            {selected === 'brands' && (
                                <FilterBrand key={'brands-filter'} category={category} list={list} form={form} />
                            )}
                            {selected === 'sizes' && (
                                <FilterSize key={'sizes-filter'} category={category} list={list} form={form} />
                            )}
                            {selected === 'price' && <FilterPrice key={'price-filter'} form={form} />}
                        </div>
                        {isDirty && (
                            <button
                                type={'button'}
                                onClick={() => {
                                    setState(p => ({
                                        ...p,
                                        open: false,
                                        selected: undefined,
                                        filter: productFilterSchema.parse({}),
                                    }));
                                }}
                                className={
                                    'mx-page bg-neutral-300 py-2 text-center font-600 text-16 text-neutral-950 uppercase outline-2 outline-neutral-500 outline-offset-2 focus-visible:outline'
                                }
                            >
                                Сбросить
                            </button>
                        )}
                        <button
                            type={'submit'}
                            className={
                                'mx-page bg-neutral-950 py-2 text-center font-600 text-16 text-neutral-50 uppercase outline-2 outline-neutral-950 outline-offset-2 focus-visible:outline'
                            }
                        >
                            Применить
                        </button>
                    </form>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
